import { useEffect, useRef, useCallback } from "react";
import i18n from "i18next";
import { useVirtualizer } from "@tanstack/react-virtual";
import { renderLatexToElement } from "@/lib/katex-config";
import { useMathMacrosVersion } from "@/lib/useMathMacros";
import "katex/dist/katex.min.css";
import { renderMarkersToHtml } from "@/lib/render-markers";
import { resolveImageSrc } from "@/lib/placeholder-image";
import { cellSpan, cellText, coveredCells } from "@/lib/tables/cells";
import styles from "./BlockRenderer.module.scss";
import { rowColorsFromBlock, rowColour } from "@/components/editor/table-grid/tableModel";
import { TitleBlockView } from "@/components/editor/blocks/TitleBlockView";
import { formatDocumentDateDisplay } from "@/lib/document-date";

/**
 * Minimal interface for a renderable block.
 * Satisfied by both Block (editor) and ReviewBlock (import review).
 */
export interface RenderableBlock {
  id?: string;
  type: string;
  content: Record<string, unknown>;
}

/**
 * Renders a single block's content as a visual preview.
 * Shared between the editor preview panel and the import review preview.
 */
export function BlockRenderer({ block }: { block: RenderableBlock }) {
  const content = block.content;

  switch (block.type) {
    case "paragraph":
      return <ParagraphPreview content={content} />;

    case "abstract":
      return (
        <div className={styles.abstract}>
          <h3 className={styles.abstractTitle}>Abstract</h3>
          <p className={styles.abstractContent} dangerouslySetInnerHTML={{ __html: sanitizeToHtml(String(content.text ?? content.html ?? "")) }} />
        </div>
      );

    case "heading":
    case "header": {
      const level = Math.min(Math.max(Number(content.level ?? 1), 1), 6);
      const text = stripHtmlTags(String(content.text ?? ""));
      if (!text) return null; // Skip empty/whitespace-only headings
      const className = `${styles.heading} ${styles[`heading${level}`] ?? ""}`;
      // Inline markdown — keep heading bold/italic/code parity with
      // paragraph/blockquote/list rendering. Pre-fix: `**Bold** title`
      // showed the asterisks literally.
      const html = sanitizeToHtml(text);
      const props = { className, dangerouslySetInnerHTML: { __html: html } };
      if (level === 1) return <h1 {...props} />;
      if (level === 2) return <h2 {...props} />;
      if (level === 3) return <h3 {...props} />;
      if (level === 4) return <h4 {...props} />;
      if (level === 5) return <h5 {...props} />;
      return <h6 {...props} />;
    }

    case "equation":
      return <EquationPreview content={content} />;

    case "code": {
      const language = content.language ? String(content.language) : "";
      return (
        <div className={styles.code}>
          {language && (
            <div className={styles.codeHeader}>
              <span className={styles.codeLanguage}>{language}</span>
            </div>
          )}
          <pre className={styles.codeBody}>
            <code>{String(content.code ?? content.text ?? "")}</code>
          </pre>
        </div>
      );
    }

    case "figure":
    case "image": {
      const src = String(content.url ?? content.src ?? "");
      const caption = String(content.caption ?? "");
      const alt = String(content.alt ?? caption ?? "Image");
      return (
        <figure className={styles.figure}>
          {src ? (
            <img src={resolveImageSrc(src)} alt={alt} className={styles.figureImage} />
          ) : (
            <div className={styles.figurePlaceholder}>[Image: {alt}]</div>
          )}
          {caption && <figcaption className={styles.figureCaption}>{caption}</figcaption>}
        </figure>
      );
    }

    case "table": {
      // Cells are strings or {content | text, colspan, rowspan} — the shape
      // imports write. Read them as the editor and the exporters do.
      const rows = Array.isArray(content.rows) ? (content.rows as unknown[][]) : [];
      const headers = Array.isArray(content.headers) ? (content.headers as unknown[]) : [];
      const caption = typeof content.caption === "string" ? content.caption : "";
      const covered = coveredCells(rows);
      return (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            {caption && <caption>{caption}</caption>}
            {headers.length > 0 && (
              <thead>
                <tr>
                  {headers.map((header, i) => (
                    <th key={i} dangerouslySetInnerHTML={{ __html: renderMarkersToHtml(cellText(header)) }} />
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} style={{ background: rowColour(rowColorsFromBlock(content.rowColors), i) }}>
                  {(Array.isArray(row) ? row : []).map((cell, j) => {
                    if (covered.has(`${i}:${j}`)) return null;
                    const { colspan, rowspan } = cellSpan(cell);
                    return (
                      <td
                        key={j}
                        colSpan={colspan > 1 ? colspan : undefined}
                        rowSpan={rowspan > 1 ? rowspan : undefined}
                        dangerouslySetInnerHTML={{ __html: renderMarkersToHtml(cellText(cell)) }}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case "theorem": {
      const theoremType = String(content.theoremType ?? "theorem");
      const theoremTitle = content.title ? String(content.title) : null;
      const theoremText = String(content.text ?? "");
      const isProof = theoremType === "proof";
      return (
        <div className={`${styles.theorem} ${styles[`theorem_${theoremType}`] ?? ""}`}>
          <div className={styles.theoremHeader}>
            <strong>{theoremType.charAt(0).toUpperCase() + theoremType.slice(1)}</strong>
            {theoremTitle && <span className={styles.theoremTitle}> ({theoremTitle})</span>}
            .
          </div>
          <div className={`${styles.theoremBody} ${isProof ? "" : styles.italic}`} dangerouslySetInnerHTML={{ __html: sanitizeToHtml(theoremText) }} />
          {isProof && <div className={styles.qed}>&#9633;</div>}
        </div>
      );
    }

    case "list": {
      type ListItemShape = string | { text?: string; richText?: Array<{ text?: string }>; checked?: boolean };
      const rawItems = content.items as ListItemShape[] | undefined;
      const listKind = (content.kind as string | undefined)
        ?? (content.ordered || content.listType === "ordered" ? "numbered" : "bullet");
      const isChecklist = listKind === "checklist";
      const ListTag = listKind === "numbered" ? "ol" : "ul";
      const isChecked = (item: ListItemShape): boolean =>
        typeof item === "object" && !!item?.checked;
      return (
        <ListTag
          className={styles.list}
          style={isChecklist ? { listStyleType: "none", paddingLeft: 4 } : undefined}
        >
          {rawItems?.map((item, i) =>
            isChecklist ? (
              <li
                key={i}
                style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 4 }}
              >
                <input
                  type="checkbox"
                  checked={isChecked(item)}
                  readOnly
                  style={{
                    marginTop: 4,
                    width: 14,
                    height: 14,
                    accentColor: "var(--lilia-primary)",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    textDecoration: isChecked(item) ? "line-through" : "none",
                    color: isChecked(item) ? "var(--lilia-text-muted)" : undefined,
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizeToHtml(extractItemText(item)) }}
                />
              </li>
            ) : (
              <li key={i} dangerouslySetInnerHTML={{ __html: sanitizeToHtml(extractItemText(item)) }} />
            ),
          ) ?? <li dangerouslySetInnerHTML={{ __html: sanitizeToHtml(String(content.text ?? content.html ?? "")) }} />}
        </ListTag>
      );
    }

    case "blockquote":
    case "quote":
      return (
        <blockquote className={styles.blockquote} dangerouslySetInnerHTML={{ __html: sanitizeToHtml(String(content.text ?? content.html ?? "")) }} />
      );

    case "bibliography":
      return (
        <div className={styles.bibliography}>
          <h3 className={styles.bibliographyTitle}>References</h3>
          <p className={styles.bibliographyPlaceholder}>[Bibliography entries will be rendered here]</p>
        </div>
      );

    case "tableOfContents":
    case "toc":
      return (
        <div className={styles.toc}>
          <h3 className={styles.tocTitle}>Table of Contents</h3>
          <p className={styles.tocPlaceholder}>[Auto-generated from document headings]</p>
        </div>
      );

    case "pageBreak":
    case "page_break":
    case "divider":
      return (
        <div className={styles.pageBreak}>
          <span className={styles.pageBreakLabel}>Page Break</span>
        </div>
      );

    case "columnBreak":
    case "column_break":
      return (
        <div className={styles.columnBreak}>
          <span className={styles.columnBreakLabel}>Column Break</span>
        </div>
      );

    // ── CV-family blocks ────────────────────────────────────────────
    // The server-side renderers in RenderService.cs occasionally throw
    // for these (variable-shape content), which used to surface the
    // sentinel "Error rendering block" HTML on mobile. The mobile
    // RenderedPreview now detects the sentinel and falls back here, so
    // these need readable client-side renderings.
    case "cvEntry":
    case "cventry": {
      const role = stringField(content, "role");
      const org = stringField(content, "org");
      const location = stringField(content, "location");
      const period = stringField(content, "period");
      const description = stringField(content, "description");
      return (
        <div className={styles.cvEntry}>
          <div className={styles.cvEntryHead}>
            {role && <span className={styles.cvEntryRole}>{role}</span>}
            {org && <span className={styles.cvEntryOrg}>{role ? `, ${org}` : org}</span>}
            {location && <span className={styles.cvEntryLocation}>{` — ${location}`}</span>}
            {period && <span className={styles.cvEntryPeriod}>{period}</span>}
          </div>
          {description && (
            <p
              className={styles.cvEntryDescription}
              dangerouslySetInnerHTML={{ __html: sanitizeToHtml(description) }}
            />
          )}
        </div>
      );
    }
    case "cvSection":
    case "cvsection": {
      const title = stringField(content, "title");
      return <h2 className={styles.cvSectionTitle}>{title}</h2>;
    }
    case "personalInfo":
    case "personalinfo": {
      const name = stringField(content, "name");
      const headline = stringField(content, "headline");
      const email = stringField(content, "email");
      const homepage = stringField(content, "homepage");
      const location = stringField(content, "location");
      const phones = Array.isArray(content.phones) ? (content.phones as Array<Record<string, unknown>>) : [];
      const bits: string[] = [];
      if (email) bits.push(email);
      for (const p of phones) {
        const num = typeof p.number === "string" ? p.number : "";
        if (num) bits.push(num);
      }
      if (location) bits.push(location);
      return (
        <div className={styles.cvPersonalInfo}>
          {name && <h1 className={styles.cvName}>{name}</h1>}
          {headline && <p className={styles.cvHeadline}>{headline}</p>}
          {(bits.length > 0 || homepage) && (
            <p className={styles.cvContact}>
              {bits.join(" · ")}
              {homepage && bits.length > 0 ? " · " : ""}
              {homepage && (
                <a href={homepage} target="_blank" rel="noopener noreferrer">
                  {homepage}
                </a>
              )}
            </p>
          )}
        </div>
      );
    }
    case "photo": {
      const src = stringField(content, "src");
      const sizeRaw = content.size;
      const size = typeof sizeRaw === "number" ? sizeRaw : 64;
      if (!src) {
        return <div className={styles.cvPhotoPlaceholder}>[photo]</div>;
      }
      return (
        <div className={styles.cvPhoto}>
          <img src={src} alt="" style={{ height: `${size}px` }} />
        </div>
      );
    }

    // ── Blocks the preview used to show as raw JSON (29 Oct 2026: the phone's Blocks tab) ──
    case "title":
      return (
        <TitleBlockView
          title={String(content.title ?? "")}
          author={String(content.author ?? "")}
          dateText={formatDocumentDateDisplay(String(content.date ?? ""), String(content.dateMode ?? ""))}
          showPlaceholders={false}
          withoutTestIds
        />
      );

    case "callout":
    case "aside": {
      const kind = String(content.calloutType ?? content.asideType ?? block.type);
      const heading = content.title ? String(content.title) : kind.charAt(0).toUpperCase() + kind.slice(1);
      return (
        <div className={styles.callout} data-kind={kind}>
          <div className={styles.calloutTitle}>{heading}</div>
          <div dangerouslySetInnerHTML={{ __html: sanitizeToHtml(String(content.text ?? "")) }} />
        </div>
      );
    }

    case "verse": {
      const lines = Array.isArray(content.lines) ? (content.lines as unknown[]).map(String) : [];
      return (
        <div className={styles.verse}>
          {content.title ? <div className={styles.verseTitle}>{String(content.title)}</div> : null}
          {lines.map((line, i) => (
            <div key={i} dangerouslySetInnerHTML={{ __html: sanitizeToHtml(line) || "&nbsp;" }} />
          ))}
          {content.attribution ? <div className={styles.verseAttribution}>— {String(content.attribution)}</div> : null}
        </div>
      );
    }

    case "footnote":
      return (
        <p className={styles.footnote}>
          <sup>*</sup> <span dangerouslySetInnerHTML={{ __html: sanitizeToHtml(String(content.text ?? "")) }} />
        </p>
      );

    case "algorithm": {
      type AlgorithmLine = { indent?: number; keyword?: string; text?: string; comment?: string };
      const lines = Array.isArray(content.lines) ? (content.lines as AlgorithmLine[]) : [];
      return (
        <div className={styles.algorithm}>
          <div className={styles.algorithmCaption}>
            <strong>{i18n.t("editor:latexView.blocks.type.algorithm")}</strong>{content.caption ? ` ${String(content.caption)}` : ""}
          </div>
          <ol className={styles.algorithmLines}>
            {lines.map((l, i) => (
              <li key={i} style={{ paddingLeft: `${(Number(l?.indent) || 0) * 1.25}em` }}>
                {l?.keyword ? <strong>{l.keyword} </strong> : null}
                <span dangerouslySetInnerHTML={{ __html: sanitizeToHtml(String(l?.text ?? "")) }} />
                {l?.comment ? <span className={styles.algorithmComment}> ▷ {l.comment}</span> : null}
              </li>
            ))}
          </ol>
        </div>
      );
    }

    case "embed":
      return (
        <div className={styles.code}>
          <div className={styles.codeHeader}>
            <span className={styles.codeLanguage}>{String(content.engine ?? "latex")}</span>
          </div>
          <pre className={styles.codeBody}>
            <code>{String(content.code ?? "")}</code>
          </pre>
          {content.caption ? <div className={styles.figureCaption}>{String(content.caption)}</div> : null}
        </div>
      );

    case "frontMatter":
    case "backMatter": {
      type Entry = { term?: string; definition?: string };
      const entries = Array.isArray(content.entries) ? (content.entries as Entry[]) : [];
      return (
        <div className={styles.matter}>
          {content.text ? <div dangerouslySetInnerHTML={{ __html: sanitizeToHtml(String(content.text)) }} /> : null}
          {entries.length > 0 && (
            <dl className={styles.matterEntries}>
              {entries.map((e, i) => (
                <div key={i}>
                  <dt>{String(e?.term ?? "")}</dt>
                  <dd dangerouslySetInnerHTML={{ __html: sanitizeToHtml(String(e?.definition ?? "")) }} />
                </div>
              ))}
            </dl>
          )}
        </div>
      );
    }

    case "cover":
      return (
        <div className={styles.cover}>
          {content.title ? <div className={styles.coverTitle}>{String(content.title)}</div> : null}
          {content.subtitle ? <div className={styles.coverSubtitle}>{String(content.subtitle)}</div> : null}
          {content.author ? <div>{String(content.author)}</div> : null}
        </div>
      );

    case "chapterBreak":
      return (
        <div className={styles.pageBreak}>
          <span className={styles.pageBreakLabel}>{content.label ? String(content.label) : i18n.t("blocks:chapterBreak")}</span>
        </div>
      );

    default: {
      // Anything else: its text when it has some, its fields otherwise, readable in both themes.
      const text = typeof content.text === "string" ? content.text : "";
      return (
        <div className={styles.unknown}>
          <p className={styles.unknownType}>{block.type}</p>
          {text ? (
            <div dangerouslySetInnerHTML={{ __html: sanitizeToHtml(text) }} />
          ) : (
            <pre className={styles.unknownContent}>
              {JSON.stringify(content, null, 2)}
            </pre>
          )}
        </div>
      );
    }
  }
}

function stringField(content: Record<string, unknown>, key: string): string {
  const v = content[key];
  return typeof v === "string" ? v : "";
}

export function extractItemText(item: string | { text?: string; richText?: Array<{ text?: string }> }): string {
  if (typeof item === "string") return item;
  if (item?.text) return item.text;
  if (item?.richText && Array.isArray(item.richText)) {
    return item.richText.map((span) => span?.text ?? "").join("");
  }
  return String(item ?? "");
}

/**
 * Strip HTML tags from text, converting <br> to newlines.
 * Used for headings and other plain-text contexts.
 */
function stripHtmlTags(raw: string): string {
  return raw
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .trim();
}

/**
 * Sanitize text for HTML rendering: escape dangerous HTML, render LML
 * inline formatting, LaTeX command tokens (cite/ref/url/footnote),
 * and KaTeX inline math. Used for theorems, blockquotes, abstracts,
 * list items — anywhere block content needs the full inline surface.
 *
 * Delegates to renderMarkersToHtml (the canonical inline renderer) so
 * the preview is consistent across block types. Without this, only
 * paragraphs rendered $math$ via KaTeX while theorems / abstracts /
 * lists showed literal `$g^a$` text — a real user-reported bug.
 */
export function sanitizeToHtml(raw: string): string {
  let html = renderMarkersToHtml(raw);
  // Restore safe inline structural tags users may have authored
  // (subscripts, superscripts, underline). renderMarkersToHtml only
  // restores <br>; theorem/abstract content sometimes carries these.
  html = html.replace(/&lt;(\/?(strong|em|b|i|sub|sup))&gt;/gi, "<$1>");
  // Newlines → <br>. LaTeX export and source view both preserve line
  // breaks inside text, so the preview should too. KaTeX-rendered
  // math spans don't contain raw newlines so this is safe to run last.
  html = html.replace(/\r\n|\r|\n/g, "<br>");
  return html;
}

/**
 * Paragraph with inline math rendering via KaTeX and HTML support.
 */
function ParagraphPreview({ content }: { content: Record<string, unknown> }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const text = String(content.text ?? content.html ?? "");

  useEffect(() => {
    if (!ref.current) return;
    renderInlineMath(ref.current);
  }, [text]);

  const html = sanitizeToHtml(text);

  return <p ref={ref} className={styles.paragraph} dangerouslySetInnerHTML={{ __html: html }} />;
}

/**
 * Equation block with KaTeX rendering.
 */
function EquationPreview({ content }: { content: Record<string, unknown> }) {
  const ref = useRef<HTMLDivElement>(null);
  const latex = String(content.latex ?? content.expression ?? "");
  const displayMode = Boolean(content.displayMode ?? true);

  // The document's macros, as the editor and the PDF use them (18 Oct).
  const macrosVersion = useMathMacrosVersion();
  useEffect(() => {
    if (!ref.current || !latex) return;
    renderLatexToElement(latex, ref.current, { displayMode, throwOnError: false });
  }, [latex, displayMode, macrosVersion]);

  return <div ref={ref} className={styles.equation}>{latex}</div>;
}

/**
 * Renders KaTeX inline math ($...$) within an element.
 */
function renderInlineMath(container: HTMLElement) {
  const text = container.textContent ?? "";
  if (!text.includes("$")) return;

  // Split on $$…$$ (display) and $…$ (inline) — display first so the
  // single-dollar regex doesn't eat half of a `$$…$$` pair. Empty math
  // (e.g. `$$ $$` placeholder, `$  $`) is left alone so users can see
  // where the cursor needs to go.
  type Part = string | { latex: string; display: boolean };
  const parts: Part[] = [];
  let last = 0;
  const regex = /\$\$([^$]+?)\$\$|\$([^$\n]+?)\$/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const isDisplay = match[1] !== undefined;
    const latex = (isDisplay ? match[1] : match[2]).trim();
    if (latex) parts.push({ latex, display: isDisplay });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  if (parts.length <= 1) return;

  container.innerHTML = "";
  for (const part of parts) {
    if (typeof part === "string") {
      container.appendChild(document.createTextNode(part));
    } else {
      const span = document.createElement("span");
      span.className = styles.inlineMath ?? "";
      renderLatexToElement(part.latex, span, { displayMode: part.display, throwOnError: false });
      container.appendChild(span);
    }
  }
}

/** Threshold: above this count, use virtualization */
const VIRTUALIZE_THRESHOLD = 200;

/**
 * Renders a list of blocks as a document preview.
 * Uses simple rendering for small documents, virtualized list for large ones.
 */
export function DocumentPreview({ blocks }: { blocks: RenderableBlock[] }) {
  if (blocks.length > VIRTUALIZE_THRESHOLD) {
    return <VirtualizedDocumentPreview blocks={blocks} />;
  }

  return (
    <div className={styles.document}>
      {blocks.map((block, i) => (
        <div key={block.id ?? i} id={`block-preview-${block.id ?? i}`} className={styles.blockWrapper}>
          <BlockRenderer block={block} />
        </div>
      ))}
    </div>
  );
}

/**
 * Virtualized document preview for large documents (200+ blocks).
 * Only renders blocks visible in the viewport + overscan.
 */
function VirtualizedDocumentPreview({ blocks }: { blocks: RenderableBlock[] }) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: blocks.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60,
    overscan: 10,
  });

  const scrollToBlock = useCallback((blockId: string) => {
    const index = blocks.findIndex((b) => b.id === blockId);
    if (index >= 0) {
      virtualizer.scrollToIndex(index, { align: "start", behavior: "smooth" });
    }
  }, [blocks, virtualizer]);

  // Expose scroll function via custom event
  useEffect(() => {
    function handleScrollTo(e: Event) {
      const blockId = (e as CustomEvent<string>).detail;
      if (blockId) scrollToBlock(blockId);
    }
    window.addEventListener("preview-scroll-to", handleScrollTo);
    return () => window.removeEventListener("preview-scroll-to", handleScrollTo);
  }, [scrollToBlock]);

  return (
    <div ref={parentRef} className={styles.virtualContainer}>
      <div
        className={styles.document}
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          position: "relative",
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const block = blocks[virtualItem.index];
          return (
            <div
              key={block.id ?? virtualItem.index}
              id={`block-preview-${block.id ?? virtualItem.index}`}
              ref={virtualizer.measureElement}
              data-index={virtualItem.index}
              className={styles.blockWrapper}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <BlockRenderer block={block} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
